/**
 * Rituals Tab
 * Displays community rituals in a 2-column grid with search
 * Conditionally renders AdminRitualsTab for admins
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { RitualCard } from "@/components/cards/ritualCard"
import { BlurGate, GuestAction } from "@/components/guest"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { useCommunityRituals } from "@/features/rituals/hooks/useCommunityRituals"
import { formatRitualType } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import type { CommunityDetail } from "../types/community-detail"
import { AdminRitualsTab } from "./AdminRitualTab"

interface RitualsTabProps {
  communityId: string
  searchQuery?: string
  isJoined?: boolean
  isPending?: boolean
  permissions?: CommunityDetail["permissions"]
  whoCanAddRituals?: string[]
  showSearch?: boolean
  onToggleSearch?: () => void
  onSearchChange?: (value: string) => void
  isGuest?: boolean
}

export function RitualsTab({
  communityId,
  searchQuery = "",
  isJoined,
  isPending,
  permissions,
  whoCanAddRituals = [],
  showSearch,
  onToggleSearch,
  onSearchChange,
  isGuest = false,
}: RitualsTabProps) {
  // Hooks MUST be called before any early returns (React Rules of Hooks)
  const { t } = useTranslation("communities")
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("all")
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Toggle save mutation
  const { mutate: toggleSave } = useToggleRitualSave()

  // Use external searchQuery, debounced
  const debouncedSearch = useDebounce(searchQuery, 500)

  // Fetch rituals with debounced search and status filter
  const { data, isLoading, isError } = useCommunityRituals(communityId, {
    limit: 50,
    search: debouncedSearch || undefined,
    status: activeTab as "all" | "archived" | "requests",
  })

  // If user has admin access, render AdminRitualsTab (after hooks)
  if (permissions?.canAddRituals) {
    return (
      <AdminRitualsTab
        communityId={communityId}
        searchQuery={searchQuery}
        isJoined={isJoined}
        isPending={isPending}
        permissions={permissions}
        showSearch={showSearch}
        onToggleSearch={onToggleSearch}
        onSearchChange={onSearchChange}
      />
    )
  }

  const totalCount = data?.total || 0

  // Transform API data to match RitualCardProps
  const rituals =
    data?.rituals.map(ritual => ({
      id: ritual.id,
      slug: ritual.slug ?? undefined,
      image: ritual.uploads?.find(u => u.isCover)?.filePath || undefined,
      userAvatar: ritual.createdBy?.coverImage || undefined,
      username: ritual.createdBy?.name || t("rituals.unknown"),
      userHandle: ritual.createdBy?.username ? `@${ritual.createdBy.username}` : "",
      title: ritual.title,
      tags: [formatRitualType(ritual.type)],
      duration: formatDuration(ritual.duration),
      contextualTag: formatRitualType(ritual.type),
      userId: ritual.createdBy.id,
      price: ritual.price,
      participationFee: ritual.participationFee,
      organizersCount: ritual.organizerCount,
      isBookmarked: ritual.isSaved ?? false,
      onBookmarkClick: isGuest ? () => {} : () => toggleSave(ritual.id),
    })) || []

  return (
    <div>
      {/* Header with tabs */}
      <div className="flex items-center justify-start mb-2.5 md:mb-4 md:mt-10 gap-4 h-10">
        <h2 className="text-lg font-semibold text-primary font-montserrat uppercase">
          {t("rituals.title")}
        </h2>
        <div className="hidden md:flex items-center border-l border-body">
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
              activeTab === "all"
                ? "border-primary text-charcoal"
                : "border-transparent text-stone hover:text-charcoal"
            }`}
          >
            {t("rituals.all")} ({totalCount})
          </button>
        </div>

        {/* Mobile search toggle */}
        <div className="md:hidden ml-auto flex items-center">
          {showSearch ? (
            <div className="flex items-center gap-2 rounded-md border border-card bg-card px-3 py-1.5">
              <Search className="w-4 h-4 text-ash shrink-0" />
              <Input
                type="text"
                placeholder={t("searchPlaceholder")}
                value={searchQuery}
                onChange={e => onSearchChange?.(e.target.value)}
                className="max-w-40 h-5 border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                autoFocus
              />
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 h-6 w-6 text-ash"
                onClick={onToggleSearch}
                aria-label="Close search"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              className="opacity-50 cursor-pointer"
              onClick={() => (isGuest ? setShowLoginModal(true) : onToggleSearch?.())}
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-charcoal" />
            </button>
          )}
        </div>
      </div>
      <div className="md:hidden flex items-center">
        <button
          type="button"
          onClick={() => setActiveTab("all")}
          className={`text-sm font-semibold transition-colors cursor-pointer ${
            activeTab === "all"
              ? "border-primary text-primary"
              : "border-transparent text-stone hover:text-primary"
          }`}
        >
          {t("rituals.all")} ({totalCount})
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-stone">{t("rituals.loading")}</p>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-red">{t("rituals.error")}</p>
        </div>
      )}

      {/* Rituals Grid */}
      {!isLoading && !isError && (
        <div className="">
          {rituals.length === 0 ? (
            <div className="md:h-[360px] py-7 w-full flex flex-col items-center md:items-start justify-center md:justify-start text-center">
              <p className="text-lg font-semibold text-charcoal mb-0 text-[18px] font-monteserat uppercase">
                {t("rituals.emptyTitle")}
              </p>
              {isJoined && !permissions?.canAddRituals ? (
                <p className="text-sm mt-4 text-stone">
                  {whoCanAddRituals.length > 0
                    ? t("rituals.emptyRestrictedWithRoles", { roles: whoCanAddRituals.join(", ") })
                    : t("rituals.emptyRestricted")}
                </p>
              ) : (
                <p className="text-sm mt-4 text-stone">{t("rituals.emptyDescription")}</p>
              )}
              {!isJoined &&
                !isPending &&
                (isGuest ? (
                  <GuestAction>
                    <Button variant="primary" className="mt-6 px-20 uppercase">
                      {t("rituals.emptyJoinCommunity")}
                    </Button>
                  </GuestAction>
                ) : (
                  <Button
                    variant="primary"
                    className="mt-6 px-20 uppercase"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent("community-join-click"))
                    }}
                  >
                    {t("rituals.emptyJoinCommunity")}
                  </Button>
                ))}
              {isJoined && !isPending && permissions?.canAddRituals && (
                <Button
                  variant="primary"
                  className="mt-6 px-20 uppercase"
                  onClick={() => {
                    navigate(`/rituals/create?communityId=${communityId}`, {
                      state: { canAddCommunityAsOrganizer: permissions?.canAddRituals },
                    })
                  }}
                >
                  {t("rituals.emptyAddRitual")}
                </Button>
              )}
            </div>
          ) : isGuest ? (
            <BlurGate ctaVariant="default">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-5 md:my-9">
                {rituals.map(ritual => (
                  <div key={ritual.id} className="h-[440px]">
                    <RitualCard {...ritual} />
                  </div>
                ))}
              </div>
            </BlurGate>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-5 md:my-9">
              {rituals.map(ritual => (
                <div key={ritual.id} className="h-[260px] md:h-[440px]">
                  <RitualCard {...ritual} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
